import React, { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import "./lib/firebase";
import "bootstrap/dist/css/bootstrap.min.css";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./App.css";

const Home = lazy(() =>
  import(/* webpackChunkName: "home" */ "./pages/marketing/Home"),
);
const Register = lazy(() =>
  import(/* webpackChunkName: "register" */ "./pages/auth/Register"),
);
const SignIn = lazy(() =>
  import(/* webpackChunkName: "sign-in" */ "./pages/auth/SignIn"),
);
const CallCenterRegistration = lazy(() =>
  import(
    /* webpackChunkName: "call-center-registration" */ "./pages/auth/CallCenterRegistration"
  ),
);
const SubmitInquiry = lazy(() =>
  import(/* webpackChunkName: "submit-inquiry" */ "./pages/inquiry/SubmitInquiry"),
);
const BuildVa = lazy(() =>
  import(/* webpackChunkName: "build-va" */ "./pages/marketing/BuildVa"),
);
const Blogs = lazy(() =>
  import(/* webpackChunkName: "blogs" */ "./pages/marketing/Blogs"),
);
const Pricing = lazy(() =>
  import(/* webpackChunkName: "pricing" */ "./pages/payments/Pricing"),
);
const About = lazy(() =>
  import(/* webpackChunkName: "about" */ "./pages/marketing/About"),
);
const Account = lazy(() =>
  import(/* webpackChunkName: "account" */ "./pages/account/Account"),
);
const ListUpload = lazy(() =>
  import(/* webpackChunkName: "list-upload" */ "./pages/skiptracing/ListUpload"),
);
const ListHistory = lazy(() =>
  import(/* webpackChunkName: "skiptracing" */ "./pages/skiptracing/ListHistory"),
);
const ForgetPassword = lazy(() =>
  import(/* webpackChunkName: "forget-password" */ "./pages/auth/ForgetPassword"),
);
const ResetPassword = lazy(() =>
  import(/* webpackChunkName: "reset-password" */ "./pages/auth/ResetPassword"),
);
const NotFound = lazy(() =>
  import(/* webpackChunkName: "not-found" */ "./pages/marketing/NotFound"),
);
const PaymentHistory = lazy(() =>
  import(
    /* webpackChunkName: "payment-history" */ "./pages/payments/PaymentHistory"
  ),
);
const ContactPricing = lazy(() =>
  import(
    /* webpackChunkName: "contact-pricing" */ "./pages/payments/ContactPricing"
  ),
);
const Policy = lazy(() =>
  import(/* webpackChunkName: "privacy-policy" */ "./pages/marketing/Policy"),
);
const Terms = lazy(() =>
  import(/* webpackChunkName: "terms" */ "./pages/marketing/Terms"),
);

function App() {
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<Root />}>
          <Route index element={<Home />} />
          <Route path="/Register" element={<Register />} />
          <Route path="/SignIn" element={<SignIn />} />
          <Route path="/CallCenterRegistration" element={<CallCenterRegistration />} />
          <Route path="/SubmitInquiry" element={<SubmitInquiry />} />
          <Route path="/build-va-team" element={<BuildVa />} />
          <Route path="/Blogs" element={<Blogs />} />
          <Route path="/Pricing" element={<Pricing />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/Account" element={<Account />} />
          <Route path="/ListUpload" element={<ListUpload />} />
          <Route path="/skiptracing" element={<ListHistory />} />
          <Route path="/ResetPassword" element={<ResetPassword />} />
          <Route path="/ForgetPassword" element={<ForgetPassword />} />
          <Route path="/PaymentHistory" element={<PaymentHistory />} />
          <Route path="/ContactPricing" element={<ContactPricing />} />
          <Route path="/privacy-policy" element={<Policy />} />
          <Route path="/terms-and-conditions" element={<Terms />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </>,
    ),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Elements stripe={stripePromise}>
        <div className={`app`}>
          <RouterProvider router={router}></RouterProvider>
        </div>
      </Elements>
    </QueryClientProvider>
  );
}

export default App;

const Root = () => {
  return (
    <div className={`App`}>
      <ScrollToTop />

      <Navbar />
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </div>
  );
};
